import { useQuery } from '@apollo/client'
import { Autocomplete, Box, Divider, TextField } from '@mui/material'
import { ProcessStatusGroup } from '__generated__/graphql'
import { type FC, useState } from 'react'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'
import { type CandidateFilterState } from 'shared/hooks/candidateSearchUrlState'
import { FilterWrapper, renderMinimalTags } from './FilterPrimitives'
import { minimalInputSx } from './minimalFilterStyles'

interface ProcessStatusOption {
    id: string
    label: string
    type: 'group' | 'status' | 'divider'
    groupLabel: string
}

interface ProcessStatusFilterSectionProps {
    filters: CandidateFilterState
    updateFilters: (patch: Partial<CandidateFilterState>) => void
}

export const ProcessStatusFilterSection: FC<
    ProcessStatusFilterSectionProps
> = ({ filters, updateFilters }) => {
    const [open, setOpen] = useState(false)
    const { data, loading } = useQuery(GetProcessStatuses)

    const OUT_OF_PROCESS_SENTINEL = 'OUT_OF_PROCESS'
    const rawStatuses = data?.processStatuses ?? []
    const outOfProcessGroup = rawStatuses.find(
        (ps) =>
            ps.processStatusSummaryGroup?.summaryGroup ===
            ProcessStatusGroup.OutOfProcess
    )?.processStatusSummaryGroup
    const outOfProcessGroupLabel = outOfProcessGroup?.label ?? 'Out of Process'

    const allOptions: ProcessStatusOption[] = rawStatuses
        .slice()
        .sort(
            (a, b) =>
                (a.processStatusSummaryGroup?.label ?? '').localeCompare(
                    b.processStatusSummaryGroup?.label ?? ''
                ) || a.statusLabel.localeCompare(b.statusLabel)
        )
        .map((ps) => ({
            id: ps.id,
            label: ps.statusLabel,
            type: 'status' as const,
            groupLabel: ps.processStatusSummaryGroup?.label ?? 'No Group',
            groupId: ps.processStatusSummaryGroup?.id ?? '',
        }))
        .reduce<ProcessStatusOption[]>((acc, opt) => {
            const lastGroup = acc.findLast((o) => o.type === 'group')
            if (lastGroup?.groupLabel !== opt.groupLabel) {
                acc.push({
                    id: opt.groupId,
                    label: opt.groupLabel,
                    type: 'group',
                    groupLabel: opt.groupLabel,
                })
                if (opt.groupLabel === outOfProcessGroupLabel) {
                    acc.push({
                        id: OUT_OF_PROCESS_SENTINEL,
                        label: 'Out of Process',
                        type: 'status',
                        groupLabel: outOfProcessGroupLabel,
                    })
                }
            }
            acc.push(opt)
            return acc
        }, [])

    const groupShortcuts = allOptions.filter((o) => o.type === 'group')
    const divider: ProcessStatusOption = {
        id: '__divider__',
        label: '',
        type: 'divider',
        groupLabel: '',
    }
    const displayOptions = [...groupShortcuts, divider, ...allOptions]

    const selectedIds = new Set([
        ...filters.processStatusIds,
        ...filters.processStatusGroupIds,
    ])
    const selectedOptions = allOptions.filter((opt) => selectedIds.has(opt.id))
    const handleChange = (_: unknown, newValue: ProcessStatusOption[]) => {
        updateFilters({
            processStatusIds: newValue
                .filter((v) => v.type === 'status')
                .map((v) => v.id),
            processStatusGroupIds: newValue
                .filter((v) => v.type === 'group')
                .map((v) => v.id),
        })
    }

    return (
        <FilterWrapper label="Process Status">
            <Autocomplete
                multiple
                size="small"
                open={open}
                onOpen={() => setOpen(true)}
                onClose={(_, reason) => {
                    if (
                        reason !== 'selectOption' &&
                        reason !== 'removeOption'
                    ) {
                        setOpen(false)
                    }
                }}
                options={displayOptions}
                loading={loading}
                value={selectedOptions}
                onChange={handleChange}
                getOptionLabel={(option) => option.label}
                getOptionDisabled={(option) => option.type === 'divider'}
                isOptionEqualToValue={(option, value) =>
                    option.id === value.id &&
                    (option.type === 'group') === (value.type === 'group')
                }
                filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase()
                    if (!inputValue) return options
                    return options.filter(
                        (o) =>
                            o.type === 'divider' ||
                            o.label.toLowerCase().includes(inputValue)
                    )
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Type to search..."
                        sx={minimalInputSx}
                    />
                )}
                renderTags={renderMinimalTags<ProcessStatusOption>(
                    (opt) => opt.label
                )}
                renderOption={({ key, ...optionProps }, option, { index }) => {
                    if (option.type === 'divider') {
                        return <Divider key="__divider__" sx={{ my: 0.5 }} />
                    }

                    const isGroup = option.type === 'group'
                    const isShortcut = isGroup && index < groupShortcuts.length
                    const isSelected = isGroup
                        ? filters.processStatusGroupIds.includes(option.id)
                        : filters.processStatusIds.includes(option.id)

                    return (
                        <Box
                            component="li"
                            key={`${isShortcut ? 'shortcut' : option.type}-${option.id}`}
                            {...optionProps}
                            sx={{
                                fontSize: isGroup ? '0.6875rem' : '0.75rem',
                                fontWeight: isGroup ? 600 : 400,
                                py: isGroup ? 0.25 : 0.5,
                                pl: isShortcut ? 1 : isGroup ? 1 : 3,
                                letterSpacing: isGroup ? '0.05em' : 'normal',
                                bgcolor: isSelected
                                    ? 'primary.50'
                                    : isGroup
                                      ? 'grey.50'
                                      : 'inherit',
                                borderLeft: isSelected ? '3px solid' : 'none',
                                borderLeftColor: 'primary.main',
                                minHeight: 'unset',
                            }}
                        >
                            {option.label}
                        </Box>
                    )
                }}
            />
        </FilterWrapper>
    )
}
